import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { PROFILE_TYPES } from "@/i18n/translations";
import { useSeo } from "@/lib/seo";

const inputCls = "w-full border border-tertiary bg-white px-4 py-3 text-sm outline-none transition-colors duration-300 focus:border-champagne focus:ring-1 focus:ring-champagne";

export default function SignupPage() {
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", profile_type: "project_holder" });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  useSeo({ title: t("member_signup"), description: t("signup_sub"), lang, robots: "noindex,follow" });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/signup", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      await api.post("/auth/resend-verification", { email: form.email });
      toast.success(t("signup_resend"));
    } catch { /* ignore */ }
  };

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-6 py-16">
      <span className="text-xs uppercase tracking-[0.3em] text-champagne">RSTI Connect</span>
      <h1 className="mt-3 font-serif text-4xl tracking-tight text-anthracite">{t("signup_title")}</h1>
      <p className="mt-3 text-anthracite/70">{t("signup_sub")}</p>

      {done ? (
        <div className="mt-8 border border-champagne/20 bg-champagne/5 p-8 text-center" data-testid="signup-success">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-champagne text-xl text-white">✓</div>
          <p className="text-anthracite">{t("signup_success")}</p>
          <button onClick={resend} data-testid="signup-resend" className="mt-4 text-sm text-champagne underline">{t("signup_resend")}</button>
          <div className="mt-2"><Link to="/login" className="text-sm text-anthracite/60 hover:text-champagne">{t("member_login")}</Link></div>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="signup-form" className="mt-8 space-y-4 border border-tertiary bg-white p-8">
          <div>
            <label className="mb-1.5 block text-xs uppercase tracking-[0.15em] text-anthracite/50">{t("form_name")}</label>
            <input required data-testid="signup-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="mb-1.5 block text-xs uppercase tracking-[0.15em] text-anthracite/50">{t("account_type")}</label>
            <select data-testid="signup-profile-type" value={form.profile_type} onChange={(e) => set("profile_type", e.target.value)} className={inputCls}>
              {PROFILE_TYPES.map((p) => <option key={p.value} value={p.value}>{p[lang]}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-xs uppercase tracking-[0.15em] text-anthracite/50">{t("form_email")}</label>
            <input required type="email" data-testid="signup-email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="mb-1.5 block text-xs uppercase tracking-[0.15em] text-anthracite/50">{lang === "fr" ? "Mot de passe (8 caractères min.)" : "Password (8 chars min.)"}</label>
            <input required type="password" minLength={8} data-testid="signup-password" value={form.password} onChange={(e) => set("password", e.target.value)} className={inputCls} />
          </div>
          <button type="submit" disabled={loading} data-testid="signup-submit" className="w-full bg-anthracite py-3.5 text-sm font-medium text-ivory transition-colors hover:bg-black disabled:opacity-50">
            {loading ? "…" : t("member_signup")}
          </button>
          <p className="text-center text-sm text-anthracite/50">
            {lang === "fr" ? "Déjà inscrit ?" : "Already registered?"} <Link to="/login" className="text-champagne">{t("member_login")}</Link>
          </p>
        </form>
      )}
    </div>
  );
}
